// Dashboards (plan step 3.5, D98): the project's boards of panels, each a read-only SQL query
// (run through run_query, as Explore runs it) or a preset over the typed reads (every plan). A
// board runs its panels over one time range, two at a time, when it opens, when the range
// changes and on Refresh, never more often than every five minutes on its own; a panel the
// plan's rate holds back waits its turn and tries again. Members create and edit; owners and
// admins delete. Titles and names may come from an agent through MCP: shown as text, never markup.
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import {
  type CSSProperties,
  type FormEvent,
  type ReactNode,
  useEffect,
  useId,
  useState,
} from 'react'
import { isApiError } from '../api/client'
import {
  appsQuery,
  dashboardQuery,
  dashboardsQuery,
  membersQuery,
  orgQuery,
  QueryError,
  runPanel,
  useAddPanel,
  useCreateDashboard,
  useDeleteDashboard,
  useDeletePanel,
  useRenameDashboard,
  useUpdatePanel,
} from '../api/queries'
import type { Dashboard, DashboardPanel, PresetPanelResult, QueryResult } from '../api/types'
import { Confirm } from '../components/Confirm'
import { btnClass, EmptyNote, PageHead, Seg } from '../components/d'
import { Icon } from '../components/icons'
import { PanelFigure } from '../components/PanelChart'
import { deviceMeta } from '../components/Perf'
import { appLabel } from '../components/Shell'
import { ErrorState, Loading } from '../components/states'
import { type MessageId, type Translator, useI18n } from '../i18n'
import {
  AUTO_REFRESH_MS,
  byPlace,
  CHART_TYPES,
  type ChartType,
  chartOf,
  checkDraft,
  dashboardError,
  draftInput,
  draftOf,
  draftPatch,
  frameOfResult,
  isPreset,
  LIMITS,
  layoutOf,
  nextLayout,
  type PanelChart,
  type PanelDraft,
  type PanelView,
  PRESET_CHART,
  PRESET_PARAMS,
  PRESETS,
  type Problem,
  presetView,
  type RangeKey,
  RunQueue,
  rangeOf,
} from '../lib/panels'
import { errorMessage, saveDraft } from './Explore'
import { useProjectParams } from './params'
import { clampRange, type DashboardsSearch, deviceParams } from './search'

/** Runs at once per page. */
const CONCURRENT_RUNS = 2
/** The range's query parameters (apps/api read/query.ts), named in the SQL's hint. */
const PLACEHOLDERS = {
  from: '{from:DateTime}',
  to: '{to:DateTime}',
  bucket: '{bucket:UInt32}',
  example: 'WHERE ts >= {from:DateTime} AND ts < {to:DateTime}',
}
/** Times a panel held back by the rate (429) tries again before it says so. */
const MAX_WAITS = 3

const isWait = (error: unknown): error is QueryError =>
  error instanceof QueryError && error.status === 429

export function DashboardsPage() {
  const { t } = useI18n()
  const { orgId, projectId } = useProjectParams()
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as DashboardsSearch
  // The filter bar's range, as the dashboards have it (1 h to 30 days).
  const range = clampRange(search.range, 'dashboards') as RangeKey
  const boards = useQuery(dashboardsQuery(projectId))
  const org = useQuery(orgQuery(orgId))
  // Owners and admins delete. Until the role is known the action shows; the API decides.
  const role = org.data?.role
  const canDelete = role === undefined || role === 'owner' || role === 'admin'
  const [queue] = useState(() => new RunQueue(CONCURRENT_RUNS))
  const [tick, setTick] = useState(0)
  const [auto, setAuto] = useState(false)
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    if (!auto) return
    const timer = window.setInterval(() => {
      if (document.visibilityState !== 'hidden') setTick((n) => n + 1)
    }, AUTO_REFRESH_MS)
    return () => window.clearInterval(timer)
  }, [auto])

  const go = (patch: Partial<DashboardsSearch>) =>
    navigate({
      to: '.',
      search: (prev: Record<string, unknown>) => ({ ...prev, ...patch }),
    })

  const list = boards.data ?? []
  const current = list.find((b) => b.id === search.board) ?? list[0]
  const full = list.length >= LIMITS.dashboards
  const newBoard =
    creating || (boards.isSuccess && list.length === 0) ? (
      <NewBoard
        projectId={projectId}
        onCreated={(board) => {
          setCreating(false)
          go({ board: board.id })
        }}
        onCancel={list.length > 0 ? () => setCreating(false) : undefined}
      />
    ) : null

  if (!current)
    return (
      <main id="main" className="pg db-pg">
        <PageHead title={t('dash.title')} meta={t('dash.lede')} />
        {newBoard}
        {boards.isPending ? (
          <Loading />
        ) : boards.isError ? (
          isApiError(boards.error, 503) ? (
            <p role="alert" className="notice error">
              {t('dash.error.unavailable')}
            </p>
          ) : (
            <ErrorState error={boards.error} retry={() => boards.refetch()} />
          )
        ) : (
          <EmptyNote title={t('dash.none')}>{t('dash.noneBody')}</EmptyNote>
        )}
      </main>
    )

  return (
    <main id="main" className="pg db-pg">
      <Board
        key={current.id}
        orgId={orgId}
        projectId={projectId}
        board={current}
        range={range}
        tick={tick}
        queue={queue}
        canDelete={canDelete}
        onDeleted={() => go({ board: undefined })}
        newDashboard={
          <button
            type="button"
            className={btnClass()}
            aria-expanded={creating}
            disabled={full}
            onClick={() => setCreating((c) => !c)}
          >
            <Icon name="plus" />
            {t('dash.new')}
          </button>
        }
        bar={
          <div className="db-bar">
            <Seg
              label={t('dash.boards')}
              value={current.id}
              onChange={(id) => go({ board: id })}
              options={list.map((b) => ({ value: b.id, label: b.name }))}
            />
            <div className="db-r">
              <button
                type="button"
                className={btnClass(undefined, 'sm')}
                onClick={() => setTick((n) => n + 1)}
              >
                {t('dash.refresh')}
              </button>
              <label className="db-chk">
                <input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} />
                {t('dash.auto')}
              </label>
            </div>
          </div>
        }
        notes={
          <>
            {full ? <p className="note">{t('dash.full', { max: LIMITS.dashboards })}</p> : null}
            {newBoard}
          </>
        }
      />
    </main>
  )
}

/** A form in a window: its title in the bar, then the fields (the mock's .win). */
function FormWindow({
  title,
  onSubmit,
  children,
  noValidate,
}: {
  title: string
  onSubmit: (e: FormEvent) => void
  children: ReactNode
  noValidate?: boolean
}) {
  const id = useId()
  return (
    <form className="win db-form" aria-labelledby={id} onSubmit={onSubmit} noValidate={noValidate}>
      <h2 id={id} className="win-bar">
        <b>{title}</b>
      </h2>
      <div className="db-form-b">{children}</div>
    </form>
  )
}

function NewBoard({
  projectId,
  onCreated,
  onCancel,
}: {
  projectId: string
  onCreated: (board: Dashboard) => void
  onCancel?: (() => void) | undefined
}) {
  const { t } = useI18n()
  const create = useCreateDashboard(projectId)
  const [name, setName] = useState('')
  const id = useId()
  const tooLong = name.trim().length > LIMITS.title
  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (!name.trim() || tooLong) return
    create.mutate(name.trim(), { onSuccess: onCreated })
  }
  return (
    <FormWindow title={t('dash.new')} onSubmit={submit}>
      <div className="field">
        <label htmlFor={id}>{t('dash.name')}</label>
        <input
          id={id}
          className="input"
          value={name}
          maxLength={LIMITS.title + 20}
          aria-invalid={tooLong || undefined}
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      {tooLong ? <p className="hint c-crash">{t('dash.v.long', { max: LIMITS.title })}</p> : null}
      {create.isError ? (
        <p role="alert" className="notice error">
          {dashboardError(t, create.error)}
        </p>
      ) : null}
      <div className="db-acts">
        <button
          type="submit"
          className={btnClass('solid', 'sm')}
          disabled={!name.trim() || tooLong || create.isPending}
        >
          {t('dash.create')}
        </button>
        {onCancel ? (
          <button type="button" className={btnClass('ghost', 'sm')} onClick={onCancel}>
            {t('settings.cancel')}
          </button>
        ) : null}
      </div>
    </FormWindow>
  )
}

const VIA: Record<Dashboard['updated_via'], MessageId> = {
  console: 'dash.via.console',
  token: 'dash.via.token',
  oauth: 'dash.via.oauth',
  mcp: 'dash.via.mcp',
}

/** The page's one accent: the first line chart in reading order (none when there is none). */
export function accentPanelId(panels: readonly DashboardPanel[]): string | undefined {
  return panels.find((p) => chartOf(p.chart).type === 'line')?.id
}

function Board({
  orgId,
  projectId,
  board,
  range,
  tick,
  queue,
  canDelete,
  onDeleted,
  newDashboard,
  bar,
  notes,
}: {
  orgId: string
  projectId: string
  board: Dashboard
  range: RangeKey
  tick: number
  queue: RunQueue
  canDelete: boolean
  onDeleted: () => void
  /** The page's "New dashboard" button, beside this board's actions. */
  newDashboard: ReactNode
  /** The board picker, Refresh and auto refresh. */
  bar: ReactNode
  /** The page's notes and its new-dashboard form, under the bar. */
  notes: ReactNode
}) {
  const i18n = useI18n()
  const { t, relative } = i18n
  const search = useSearch({ strict: false }) as DashboardsSearch
  const detail = useQuery(dashboardQuery(projectId, board.id))
  const dashboard = detail.data?.dashboard ?? board
  const members = useQuery({ ...membersQuery(orgId), retry: false })
  const rename = useRenameDashboard(projectId)
  const remove = useDeleteDashboard(projectId)
  const removePanel = useDeletePanel(projectId)
  const [editing, setEditing] = useState<string | null>(null)
  const [renaming, setRenaming] = useState(false)
  const [problem, setProblem] = useState<unknown>(null)
  const panels = byPlace(detail.data?.panels ?? [])
  const nameOf = (id: string | null) =>
    id ? members.data?.find((m) => m.user_id === id)?.name : undefined
  const who = nameOf(dashboard.updated_by)
  const full = panels.length >= LIMITS.panels
  const values = { when: relative(dashboard.updated_at), via: t(VIA[dashboard.updated_via]) }
  const byMcp = panels.filter((p) => p.updated_via === 'mcp').length
  const accent = accentPanelId(panels)
  const addPanel = () => setEditing(editing === 'new' ? null : 'new')
  const addId = useId()

  return (
    <>
      <PageHead
        crumb={t('dash.title')}
        title={<span translate="no">{dashboard.name}</span>}
        meta={[
          detail.data ? t('dash.meta.panels', { count: panels.length }) : '',
          who ? t('dash.meta.editedBy', { ...values, who }) : t('dash.meta.edited', values),
          byMcp ? t('dash.meta.byMcp', { count: byMcp }) : '',
          // Panels run their own queries: a carried device filter is said not to apply (APS-228).
          deviceMeta(i18n, undefined, deviceParams(search), 'na') ?? '',
        ]
          .filter(Boolean)
          .join(' · ')}
        actions={
          <>
            <button
              type="button"
              className={btnClass()}
              aria-expanded={renaming}
              onClick={() => setRenaming((r) => !r)}
            >
              {t('dash.rename')}
            </button>
            {newDashboard}
            {canDelete ? (
              <Confirm
                label={t('dash.delete')}
                title={t('dash.deleteTitle')}
                body={t('dash.deleteBody', { name: dashboard.name })}
                confirm={t('dash.delete')}
                buttonClass={btnClass('ghost')}
                busy={remove.isPending}
                onConfirm={() => {
                  setProblem(null)
                  remove.mutate(dashboard.id, { onSuccess: onDeleted, onError: setProblem })
                }}
              />
            ) : null}
          </>
        }
      />
      {bar}
      {notes}
      {problem ? (
        <p role="alert" className="notice error">
          {dashboardError(t, problem)}
        </p>
      ) : null}
      {renaming ? (
        <RenameBoard
          board={dashboard}
          busy={rename.isPending}
          error={rename.error}
          onCancel={() => setRenaming(false)}
          onSave={(name) =>
            rename.mutate(
              { dashboardId: dashboard.id, name },
              { onSuccess: () => setRenaming(false) },
            )
          }
        />
      ) : null}
      {editing === 'new' ? (
        <PanelEditor
          orgId={orgId}
          projectId={projectId}
          dashboardId={dashboard.id}
          panels={panels}
          onDone={() => setEditing(null)}
        />
      ) : null}
      {detail.isPending ? (
        <Loading />
      ) : detail.isError ? (
        <ErrorState error={detail.error} retry={() => detail.refetch()} />
      ) : (
        <div className="db-grid">
          {panels.length === 0 ? (
            <EmptyNote title={t('dash.empty')}>{t('dash.emptyBody')}</EmptyNote>
          ) : null}
          {panels.map((panel) =>
            editing === panel.id ? (
              <div key={panel.id} className="db-wide">
                <PanelEditor
                  orgId={orgId}
                  projectId={projectId}
                  dashboardId={dashboard.id}
                  panel={panel}
                  panels={panels}
                  onDone={() => setEditing(null)}
                />
              </div>
            ) : (
              <PanelCard
                key={panel.id}
                orgId={orgId}
                projectId={projectId}
                panel={panel}
                range={range}
                tick={tick}
                queue={queue}
                canDelete={canDelete}
                accent={panel.id === accent}
                who={nameOf(panel.updated_by)}
                onEdit={() => setEditing(panel.id)}
                onDelete={() => {
                  setProblem(null)
                  removePanel.mutate(
                    { dashboardId: dashboard.id, panelId: panel.id },
                    { onError: setProblem },
                  )
                }}
              />
            ),
          )}
          <button
            type="button"
            className="db-new"
            disabled={full}
            aria-expanded={editing === 'new'}
            aria-describedby={addId}
            onClick={addPanel}
          >
            <strong>
              <Icon name="plus" />
              {t('dash.addPanel')}
            </strong>
            {/* Named by its title, described by this line: no aria-label, which would have to
                hold this line too (WCAG 2.5.3, axe 4.14). */}
            <span id={addId} aria-hidden="true">
              {full ? t('dash.panelsFull', { max: LIMITS.panels }) : t('dash.addPanelBody')}
            </span>
          </button>
        </div>
      )}
    </>
  )
}

function RenameBoard({
  board,
  busy,
  error,
  onSave,
  onCancel,
}: {
  board: Dashboard
  busy: boolean
  error: unknown
  onSave: (name: string) => void
  onCancel: () => void
}) {
  const { t } = useI18n()
  const [name, setName] = useState(board.name)
  const id = useId()
  const tooLong = name.trim().length > LIMITS.title
  return (
    <FormWindow
      title={t('dash.rename')}
      onSubmit={(e) => {
        e.preventDefault()
        if (name.trim() && !tooLong) onSave(name.trim())
      }}
    >
      <div className="field">
        <label htmlFor={id}>{t('dash.name')}</label>
        <input
          id={id}
          className="input"
          value={name}
          aria-invalid={tooLong || undefined}
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      {tooLong ? <p className="hint c-crash">{t('dash.v.long', { max: LIMITS.title })}</p> : null}
      {error ? (
        <p role="alert" className="notice error">
          {dashboardError(t, error)}
        </p>
      ) : null}
      <div className="db-acts">
        <button
          type="submit"
          className={btnClass('solid', 'sm')}
          disabled={!name.trim() || tooLong || busy}
        >
          {t('dash.save')}
        </button>
        <button type="button" className={btnClass('ghost', 'sm')} onClick={onCancel}>
          {t('settings.cancel')}
        </button>
      </div>
    </FormWindow>
  )
}

/** A run's answer as a view: a SQL panel's rows, or a preset's answer shaped by lib/panels. */
function viewOf(data: QueryResult | PresetPanelResult, chart: PanelChart): PanelView {
  if ('columns' in data) return { kind: 'frame', frame: frameOfResult(data), chart }
  return presetView(data.preset, data.data, chart)
}

/**
 * A panel head's mono meta, as the mock writes it: the cadence and the unit ("daily · %", "ms"),
 * or a preset's caption (the release the vitals are of). Empty when the panel says neither.
 */
function metaOf(
  t: Translator['t'],
  panel: DashboardPanel,
  view: PanelView,
  chart: PanelChart,
): string {
  if (view.kind === 'stats') return view.caption ?? ''
  if (view.kind !== 'frame') return ''
  const by = (panel.params as Record<string, unknown> | null)?.by
  const cadence =
    panel.preset === 'crash_free' || (panel.preset === 'performance_series' && by !== 'release')
      ? t('dash.meta.daily')
      : panel.preset === 'performance_series'
        ? t('dash.meta.byRelease')
        : ''
  const y = view.chart.y?.[0]
  const format = view.frame.columns.find((c) => c.name === y)?.format
  const unit = chart.unit || (format === 'percent' ? '%' : format === 'ms' ? 'ms' : '')
  return [cadence, unit].filter(Boolean).join(' · ')
}

/** A panel's run failing, in the reader's words; Explore's for the query's own errors. */
function runError(t: Translator['t'], error: unknown): string {
  if (error instanceof QueryError) {
    if (error.code === 'plan_required') return t('dash.run.plan')
    if (error.code === 'not_found') return t('dash.error.notFound')
    if (error.code === 'forbidden') return t('state.forbidden')
  }
  return errorMessage(t, error)
}

function PanelCard({
  orgId,
  projectId,
  panel,
  range,
  tick,
  queue,
  canDelete,
  accent,
  who,
  onEdit,
  onDelete,
}: {
  orgId: string
  projectId: string
  panel: DashboardPanel
  range: RangeKey
  tick: number
  queue: RunQueue
  canDelete: boolean
  /** This panel's chart holds the page's one accent. */
  accent: boolean
  /** Who last changed the panel, when they are a member. */
  who: string | undefined
  onEdit: () => void
  onDelete: () => void
}) {
  const { t, relative } = useI18n()
  const headingId = useId()
  const layout = layoutOf(panel.layout)
  const chart = chartOf(panel.chart)
  const [asTable, setAsTable] = useState(false)
  // The key is everything a run depends on: an unchanged panel over the same range and refresh
  // never runs again; an edited one (its updated_at) does, alone.
  const run = useQuery({
    queryKey: [
      'project',
      projectId,
      'panel-run',
      panel.dashboard_id,
      panel.id,
      panel.updated_at,
      range,
      tick,
    ],
    queryFn: ({ signal }) =>
      queue.run(
        () => runPanel(projectId, panel.dashboard_id, panel.id, rangeOf(range), signal),
        signal,
      ),
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 10 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    // Held back by the plan's rate or the project's concurrent queries: wait as told, try again.
    retry: (count, error) => isWait(error) && count < MAX_WAITS,
    retryDelay: (_, error) => (isWait(error) ? (error.retryAfter ?? 60) : 60) * 1000,
    // The last figure stays up while the same panel refreshes.
    placeholderData: (previous, query) =>
      query?.queryKey[5] === panel.updated_at && query?.queryKey[6] === range
        ? previous
        : undefined,
  })
  const waiting = run.isFetching && run.failureCount > 0 && isWait(run.failureReason)
  const height = Math.max(120, Math.min(layout.h * 80 - 96, 230))
  const style = { '--w': layout.w, '--wl': layout.w === 6 ? 6 : 12 } as CSSProperties
  const charted = chart.type === 'line' || chart.type === 'bar'
  const kindLabel =
    panel.kind === 'sql'
      ? t('dash.kind.sql')
      : isPreset(panel.preset)
        ? t(`dash.preset.${panel.preset}` as MessageId)
        : t('dash.kind.preset')
  const shown: PanelChart = asTable && charted ? { ...chart, type: 'table' } : chart
  const view = run.data ? viewOf(run.data, shown) : undefined
  const meta = view ? metaOf(t, panel, view, chart) : ''

  let body: ReactNode
  if (waiting)
    body = (
      <p role="status" className="note">
        {t('dash.waiting')}
      </p>
    )
  else if (run.isPending) body = <Loading />
  else if (run.isError)
    body = (
      <p role="alert" className="notice error">
        {runError(t, run.error)}
      </p>
    )
  else
    body = (
      <PanelFigure
        view={view ?? viewOf(run.data, shown)}
        label={t('dash.figure', { title: panel.title })}
        height={height}
        accent={accent && !asTable}
      />
    )

  return (
    <section className="db-p" style={style} aria-labelledby={headingId}>
      <div className="db-pb">
        <h2 id={headingId} translate="no">
          {panel.title}
        </h2>
        {meta ? <span className="m">{meta}</span> : null}
        <span className="db-r">
          {panel.kind === 'sql' && panel.sql ? (
            <Link
              className={btnClass('ghost', 'sm')}
              to="/o/$orgId/p/$projectId/explore"
              params={{ orgId, projectId }}
              onClick={() => saveDraft(projectId, panel.sql ?? '')}
            >
              {t('dash.openExplore')}
            </Link>
          ) : null}
          {charted ? (
            <button
              type="button"
              className={btnClass('ghost', 'sm')}
              aria-pressed={asTable}
              aria-label={t('dash.tableOf', { title: panel.title })}
              onClick={() => setAsTable((v) => !v)}
            >
              {t('dash.table')}
            </button>
          ) : null}
          <button
            type="button"
            className={btnClass('ghost', 'sm')}
            aria-label={t('dash.editPanel', { title: panel.title })}
            onClick={onEdit}
          >
            {t('dash.edit')}
          </button>
          {canDelete ? (
            <Confirm
              label={t('dash.delete')}
              title={t('dash.deletePanelTitle')}
              body={t('dash.deletePanelBody', { title: panel.title })}
              confirm={t('dash.delete')}
              onConfirm={onDelete}
              buttonClass={btnClass('ghost', 'sm')}
            />
          ) : null}
        </span>
      </div>
      <div className={shown.type === 'table' ? 'db-pbody tb' : 'db-pbody'}>{body}</div>
      <p className="db-pfoot">
        <span translate="no" title={kindLabel}>
          {panel.kind === 'sql'
            ? `${t('dash.kind.sql')} · ${t(`dash.chart.${chart.type}` as MessageId).toLowerCase()}`
            : `${t('dash.kind.preset').toLowerCase()} · ${panel.preset ?? ''}`}
        </span>
        {/* Narrow panels keep only when; wider ones say who and how too. */}
        <span className="db-pf-full">
          {who
            ? t('dash.panel.editedBy', {
                who,
                when: relative(panel.updated_at),
                via: t(VIA[panel.updated_via]),
              })
            : t('dash.panel.edited', {
                when: relative(panel.updated_at),
                via: t(VIA[panel.updated_via]),
              })}
        </span>
        <span className="db-pf-short">{relative(panel.updated_at)}</span>
      </p>
    </section>
  )
}

/** A field's message, for the field and its aria-describedby. */
function FieldError({ id, problem }: { id: string; problem: Problem | undefined }) {
  const { t } = useI18n()
  return problem ? (
    <span id={id} className="hint c-crash">
      {t(problem.message, problem.values)}
    </span>
  ) : null
}

function TextField({
  label,
  value,
  onChange,
  problem,
  hint,
  type = 'text',
  mono,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  problem: Problem | undefined
  hint?: string | undefined
  type?: 'text' | 'number'
  mono?: boolean
}) {
  const id = useId()
  const described = [hint ? `${id}-hint` : '', problem ? `${id}-err` : ''].filter(Boolean)
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        className={mono ? 'input mono' : 'input'}
        type={type}
        value={value}
        translate={mono ? 'no' : undefined}
        spellCheck={mono ? false : undefined}
        aria-invalid={problem ? true : undefined}
        aria-describedby={described.length ? described.join(' ') : undefined}
        onChange={(e) => onChange(e.target.value)}
      />
      {hint ? (
        <span id={`${id}-hint`} className="hint">
          {hint}
        </span>
      ) : null}
      <FieldError id={`${id}-err`} problem={problem} />
    </div>
  )
}

function SelectField({
  label,
  value,
  onChange,
  problem,
  options,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  problem: Problem | undefined
  options: { value: string; label: string }[]
}) {
  const id = useId()
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <select
        id={id}
        className="select"
        value={value}
        aria-invalid={problem ? true : undefined}
        aria-describedby={problem ? `${id}-err` : undefined}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <FieldError id={`${id}-err`} problem={problem} />
    </div>
  )
}

/** Adds a panel, or changes one: checked against the API's limits before it is sent. */
function PanelEditor({
  orgId,
  projectId,
  dashboardId,
  panel,
  panels,
  onDone,
}: {
  orgId: string
  projectId: string
  dashboardId: string
  panel?: DashboardPanel
  panels: DashboardPanel[]
  onDone: () => void
}) {
  const { t, number } = useI18n()
  const apps = useQuery(appsQuery(orgId, projectId))
  const add = useAddPanel(projectId)
  const update = useUpdatePanel(projectId)
  const [draft, setDraft] = useState<PanelDraft>(() => draftOf(panel))
  const [problems, setProblems] = useState<Problem[]>([])
  const ids = { form: useId(), sql: useId(), sqlHint: useId(), sqlErr: useId() }
  const set = (patch: Partial<PanelDraft>) => setDraft((d) => ({ ...d, ...patch }))
  const problemOf = (field: string) => problems.find((p) => p.field === field)
  const pending = add.isPending || update.isPending
  const error = panel ? update.error : add.error

  const submit = (e: FormEvent) => {
    e.preventDefault()
    const found = checkDraft(draft)
    setProblems(found)
    if (found.length || pending) return
    if (panel)
      update.mutate(
        { dashboardId, panelId: panel.id, patch: draftPatch(draft, panel) },
        { onSuccess: onDone },
      )
    else
      add.mutate(
        {
          dashboardId,
          panel: draftInput(draft, nextLayout(panels, Number(draft.w), Number(draft.h))),
        },
        { onSuccess: onDone },
      )
  }

  const appOptions = (required: boolean) => [
    { value: '', label: required ? t('dash.param.pickApp') : t('dash.param.allApps') },
    ...(apps.data ?? []).map((a) => ({ value: a.id, label: appLabel(a) })),
  ]
  const sqlProblem = problemOf('sql')
  const planRefused = error instanceof Error && 'code' in error && error.code === 'plan_required'

  return (
    <form className="win db-form" aria-labelledby={ids.form} onSubmit={submit} noValidate>
      <h2 id={ids.form} className="win-bar">
        <b>{panel ? t('dash.editTitle') : t('dash.addPanel')}</b>
      </h2>
      <div className="db-form-b">
        {problems.length ? (
          <p role="alert" className="notice error">
            {t('dash.v.fix')}
          </p>
        ) : null}
        <TextField
          label={t('dash.f.title')}
          value={draft.title}
          onChange={(title) => set({ title })}
          problem={problemOf('title')}
        />
        <fieldset className="toolbar">
          <legend className="label" style={{ fontWeight: 600, fontSize: 13.5, marginBottom: 6 }}>
            {t('dash.f.kind')}
          </legend>
          {(['sql', 'preset'] as const).map((kind) => (
            <label key={kind} className="check" style={{ marginRight: 12 }}>
              <input
                type="radio"
                name={`${ids.form}-kind`}
                checked={draft.kind === kind}
                onChange={() =>
                  set(
                    kind === 'preset' && !panel
                      ? { kind, type: PRESET_CHART[draft.preset] }
                      : { kind },
                  )
                }
              />
              {t(`dash.kind.${kind}` as MessageId)}
            </label>
          ))}
        </fieldset>
        {draft.kind === 'sql' ? (
          <div className="field">
            <label htmlFor={ids.sql}>{t('explore.sql')}</label>
            <textarea
              id={ids.sql}
              className="input mono"
              style={{ maxWidth: 'none', minHeight: 160, padding: 12, fontSize: 13 }}
              rows={8}
              spellCheck={false}
              autoCapitalize="off"
              autoCorrect="off"
              translate="no"
              aria-invalid={sqlProblem ? true : undefined}
              aria-describedby={[ids.sqlHint, sqlProblem ? ids.sqlErr : '']
                .filter(Boolean)
                .join(' ')}
              value={draft.sql}
              onChange={(e) => set({ sql: e.target.value })}
            />
            <span id={ids.sqlHint} className="hint">
              {t('dash.f.sqlHint', {
                length: number(draft.sql.length),
                max: number(LIMITS.sql),
                ...PLACEHOLDERS,
              })}
            </span>
            <FieldError id={ids.sqlErr} problem={sqlProblem} />
          </div>
        ) : (
          <>
            <SelectField
              label={t('dash.f.preset')}
              value={draft.preset}
              problem={undefined}
              options={PRESETS.map((p) => ({
                value: p,
                label: t(`dash.preset.${p}` as MessageId),
              }))}
              onChange={(value) => {
                if (isPreset(value)) set({ preset: value, params: {}, type: PRESET_CHART[value] })
              }}
            />
            <div className="form-row">
              {PRESET_PARAMS[draft.preset].map((p) => {
                const value = draft.params[p.key] ?? ''
                const onChange = (v: string) => set({ params: { ...draft.params, [p.key]: v } })
                const problem = problemOf(`param.${p.key}`)
                if (p.kind === 'app')
                  return (
                    <SelectField
                      key={p.key}
                      label={t(p.label)}
                      value={value}
                      problem={problem}
                      options={appOptions(!!p.required)}
                      onChange={onChange}
                    />
                  )
                if (p.kind === 'enum')
                  return (
                    <SelectField
                      key={p.key}
                      label={t(p.label)}
                      value={value}
                      problem={problem}
                      options={[
                        {
                          value: '',
                          label: p.required ? t('dash.param.pick') : t('dash.param.default'),
                        },
                        ...(p.values ?? []).map((v) => ({ value: v.value, label: t(v.label) })),
                      ]}
                      onChange={onChange}
                    />
                  )
                return (
                  <TextField
                    key={p.key}
                    label={t(p.label)}
                    value={value}
                    type={p.kind === 'int' ? 'number' : 'text'}
                    mono={p.kind === 'text'}
                    problem={problem}
                    hint={p.required ? undefined : t('dash.param.optional')}
                    onChange={onChange}
                  />
                )
              })}
            </div>
          </>
        )}
        <div className="form-row">
          <SelectField
            label={t('dash.f.chart')}
            value={draft.type}
            problem={undefined}
            options={CHART_TYPES.map((c) => ({
              value: c,
              label: t(`dash.chart.${c}` as MessageId),
            }))}
            onChange={(type) => set({ type: type as ChartType })}
          />
          <TextField
            label={t('dash.f.unit')}
            value={draft.unit}
            problem={problemOf('unit')}
            onChange={(unit) => set({ unit })}
          />
          <TextField
            label={t('dash.f.threshold')}
            value={draft.threshold}
            type="number"
            problem={problemOf('threshold')}
            onChange={(threshold) => set({ threshold })}
          />
        </div>
        {draft.kind === 'sql' ? (
          <div className="form-row">
            <TextField
              label={t('dash.f.x')}
              value={draft.x}
              mono
              hint={t('dash.f.xHint')}
              problem={problemOf('x')}
              onChange={(x) => set({ x })}
            />
            <TextField
              label={t('dash.f.y')}
              value={draft.y}
              mono
              hint={t('dash.f.yHint', { max: LIMITS.y })}
              problem={problemOf('y')}
              onChange={(y) => set({ y })}
            />
            <TextField
              label={t('dash.f.series')}
              value={draft.series}
              mono
              hint={t('dash.f.seriesHint')}
              problem={problemOf('series')}
              onChange={(series) => set({ series })}
            />
          </div>
        ) : null}
        <div className="form-row">
          <TextField
            label={t('dash.f.width')}
            value={draft.w}
            type="number"
            hint={t('dash.f.sizeHint')}
            problem={problemOf('w')}
            onChange={(w) => set({ w })}
          />
          <TextField
            label={t('dash.f.height')}
            value={draft.h}
            type="number"
            hint={t('dash.f.sizeHint')}
            problem={problemOf('h')}
            onChange={(h) => set({ h })}
          />
        </div>
        {error ? (
          <div role="alert" className="notice error stack">
            <p>{dashboardError(t, error)}</p>
            {planRefused && draft.kind === 'sql' ? (
              <button
                type="button"
                className="btn small"
                style={{ justifySelf: 'start' }}
                onClick={() => set({ kind: 'preset', type: PRESET_CHART[draft.preset] })}
              >
                {t('dash.usePreset')}
              </button>
            ) : null}
          </div>
        ) : null}
        <div className="db-acts">
          <button type="submit" className={btnClass('solid', 'sm')} disabled={pending}>
            {panel ? t('dash.save') : t('dash.addPanel')}
          </button>
          <button type="button" className={btnClass('ghost', 'sm')} onClick={onDone}>
            {t('settings.cancel')}
          </button>
        </div>
      </div>
    </form>
  )
}
